import { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Frame } from "../../components/layouts/Frame";
import { UserApiData } from "../../types";
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../contexts/AuthContext";

export default function Test() {
  const [queries, setQueries] = useState<string[]>([""]);
  const [detail, setDetail] = useState<"1" | "2" | "3">("3");
  const [image, setImage] = useState<any | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [apiResult, setApiResult] = useState<any | null>(null);
  const [apiError, setApiError] = useState<any | null>(null);
  const [userApi, setUserApi] = useState<UserApiData>({});
  const { isAuthenticated } = useAuth();
  const { fetchData } = useAPI();
  const [isRunning, setIsRunning] = useState<boolean>(false);

  const creditsPerDetail = {
    "1": 1,
    "2": 2,
    "3": 3,
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData("/user-api").then(({ errors, data }) => {
        setUserApi(data);
      });
    }
  }, [isAuthenticated]);

  const handleAddQuery = () => {
    setQueries([...queries, ""]);
  };

  const handleRemoveQuery = (index: number) => {
    setQueries(queries.filter((_, i) => i !== index));
  };

  const handleQueryChange = (index: number, value: string) => {
    const updatedQueries = [...queries];
    updatedQueries[index] = value;
    setQueries(updatedQueries);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImage(file);
    }
  };

  const handleBrowseClick = (event: any) => {
    fileInputRef.current?.click();
  };

  const handleDetailChange = (event: SelectChangeEvent<string>) => {
    if (event.target.value == "1") setDetail("1");
    else if (event.target.value == "2") setDetail("2");
    else setDetail("3");
  };

  const handleReset = () => {
    setQueries([""]);
    setImage(null);
    setDetail("3");
  };

  const handleRun = async () => {
    setIsRunning(true);
    const formData = new FormData();
    formData.append("public_key", userApi.public_key);
    formData.append("secret_key", userApi.secret_key);
    queries.forEach(item => {
      formData.append("query[]", item);
    });
    formData.append("detail", detail);
    formData.append("image", image);
    formData.append("is_test_bed", "1");
    const { errors, data } = await fetchData(
      "/private/extract",
      formData,
      "POST",
    );
    setIsRunning(false);
    setApiError(errors);
    setApiResult(data);
    console.log(errors, data);
  };

  return (
    <Frame title="Test Bed">
      <Stack direction="row">
        <Stack width="75%" spacing={2}>
          <Typography variant="h5" fontWeight="600" sx={{ mb: 2 }}>
            Query
          </Typography>
          <Stack spacing={1}>
            {queries.map((query, index) => (
              <Stack
                direction="row"
                alignItems="center"
                key={index}
                spacing={2}
                mb={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={query}
                  onChange={e => handleQueryChange(index, e.target.value)}
                />
                {index === queries.length - 1 && (
                  <IconButton
                    onClick={handleAddQuery}
                    sx={{
                      backgroundColor: "black",
                      color: "white",
                      borderRadius: "50%",
                      width: 36,
                      height: 36,
                      "&:hover": {
                        backgroundColor: "#333",
                      },
                    }}>
                    <AddIcon />
                  </IconButton>
                )}
                {queries.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveQuery(index)}
                    color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>

          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
            Upload file
          </Typography>
          <Box
            sx={{
              border: "2px dashed gray",
              p: 3,
              textAlign: "center",
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
              position: "relative",
              cursor: "pointer",
            }}
            onClick={handleBrowseClick}>
            {image ? (
              <img
                src={URL.createObjectURL(image)}
                alt="Uploaded File"
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <>
                <CloudUploadIcon fontSize="large" />
                <Typography variant="body1">
                  Drag and Drop Image File here
                </Typography>
                <Typography variant="body2">or</Typography>
                <Button variant="contained" color="error">
                  Browse
                </Button>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>

          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
            Detail
          </Typography>
          <Select fullWidth value={detail} onChange={handleDetailChange}>
            <MenuItem value="3">High</MenuItem>
            <MenuItem value="2">Medium</MenuItem>
            <MenuItem value="1">Low</MenuItem>
          </Select>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRun}
              disabled={isRunning}>
              Run
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              disabled={isRunning}>
              Reset
            </Button>
          </Stack>
          <Box sx={{ mt: 2 }}>
            {apiResult ? (
              <>
                <Typography variant="h6" fontWeight="600" sx={{ mt: 4 }}>
                  Results:
                </Typography>
                <Typography variant="body2">
                  Job Ref: {apiResult["Job Ref"] ?? "-"}
                </Typography>
                <Typography variant="body2">
                  Credits Used: {apiResult["Credits Used"] ?? "-"}
                </Typography>
                <Typography variant="body2">
                  Credits Remaining: {apiResult["Credits Remaining"] ?? "-"}
                </Typography>
                <Typography variant="body2">
                  Query Made: {apiResult["Query Made"] ?? "-"}
                </Typography>
                {Object.keys(apiResult).map((key: string) =>
                  [
                    "Job Ref",
                    "Credits Used",
                    "Credits Remaining",
                    "Query Made",
                  ].includes(key) ? null : (
                    <Typography variant="body2" key={key}>
                      {key}: {apiResult[key] ?? "-"}
                    </Typography>
                  ),
                )}
              </>
            ) : null}
            {apiError ? (
              <>
                <Typography
                  variant="h6"
                  color="red"
                  fontWeight="600"
                  sx={{ mt: 4 }}>
                  Error:
                </Typography>
                <Typography color="red">
                  <pre>{JSON.stringify(apiError, null, 2)}</pre>
                </Typography>
              </>
            ) : null}
          </Box>
        </Stack>
        <Box p={6}>
          <Typography variant="h6" fontWeight="600">
            This Query will Cost:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            {creditsPerDetail[detail] ?? "3"} x Credit for each query
          </Typography>
          <Typography variant="body1">
            Total: {creditsPerDetail[detail] ?? "3"} Credits
          </Typography>
        </Box>
      </Stack>
    </Frame>
  );
}
